import Vec2 from 'vec2';

import {
  ARROW_LENGTH_GRANULARITY_IN_INCHES,
  BACKSPLASH_INPUT_LIMIT,
  DEFAULT_CORNER_RADIUS,
  EDGE_LENGTH_INPUT_LIMIT,
  MINIMUM_ANGLE_TO_ENFORCE_GAP,
  MINIMUM_GAP,
  WATERFALL_INPUT_LIMIT,
} from '~/core/constants';
import { calculateIsoscelesTriangleSide } from '~/core/engine/DrawingTool/utils';
import { SideItemOption, isBumpOut, isRadiusOut } from '~/core/enums';
import {
  adjustEdgeForDiagonals,
  angleBetween,
  convertToRadians,
  findArrowByEdgeId,
  findDrawingEdgeById,
  isSameSign,
  roundToArrowLengthGranularity,
  roundToCornerRadiusGranularity,
  roundToNearest,
  subtractVectors,
} from '~/core/helpers';
import {
  ArrowInfo,
  CornerInfo,
  DrawingEdge,
  DrawingShape,
} from '~/core/models';
import { clampWithLimit } from '~/core/utils';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Context for processing arrow constraints.
 * Contains pre-fetched data to avoid repeated lookups.
 */
interface ArrowConstraintContext {
  curArrow: ArrowInfo;
  curEdge: DrawingEdge;
  parallelEdge: DrawingEdge;
  parallelArrow: ArrowInfo;
  skIndex: number;
  shape: DrawingShape;
}

/**
 * Context for edge maximum calculation.
 * Contains pre-computed values for clearance checks.
 */
interface EdgeMaxContext {
  shape: DrawingShape;
  edge: DrawingEdge;
  edgeSkIndex: number;
  isBeginningOfSkeleton: boolean;
  startCoord: Vec2;
  endCoord: Vec2;
  isHorizontalEdge: boolean;
}

/**
 * Represents a keep-out rectangle with top-right and bottom-left corners.
 */
interface KeepOutRectangle {
  tr: Vec2;
  bl: Vec2;
}

/**
 * Direction extraction function type.
 * Selects x or y component based on edge orientation.
 */
type DirectionExtractor = (point: Vec2) => number;

// ============================================================================
// SKELETON INDEX HELPERS
// ============================================================================

/**
 * Gets the skeleton index for an edge based on its corner positions.
 */
const getSkeletonIndex = (shape: DrawingShape, edge: DrawingEdge): number => {
  const startCorner = shape.cornerMap[edge.startCornerId];
  const endCorner = shape.cornerMap[edge.endCornerId];

  if (startCorner.isUpper) {
    return startCorner.skIndex;
  }

  return endCorner.skIndex;
};

/**
 * Checks if edge is interior (not at skeleton beginning or end).
 */
const isInteriorSkeletonEdge = (
  skIndex: number,
  skeletonLength: number
): boolean => skIndex !== 0 && skIndex < skeletonLength - 2;

// ============================================================================
// KEEP-OUT ZONE HELPERS
// ============================================================================

/**
 * Builds a rectangular keep-out zone for an edge.
 */
const buildKeepOutRectangle = (
  edge: DrawingEdge,
  shape: DrawingShape
): KeepOutRectangle => {
  const rect: KeepOutRectangle = {
    tr: new Vec2(0, 0),
    bl: new Vec2(0, 0),
  };
  const startCoord = shape.cornerMap[edge.startCornerId].coords;
  const endCoord = shape.cornerMap[edge.endCornerId].coords;

  if (Math.abs(edge.dirVec.x) > Math.abs(edge.dirVec.y)) {
    // Edge is horizontal
    if (edge.dirVec.x > 0) {
      rect.tr = new Vec2(endCoord.x + MINIMUM_GAP, endCoord.y - MINIMUM_GAP);
      rect.bl = new Vec2(
        startCoord.x - MINIMUM_GAP,
        startCoord.y + MINIMUM_GAP
      );
    } else {
      rect.tr = new Vec2(
        startCoord.x + MINIMUM_GAP,
        startCoord.y - MINIMUM_GAP
      );
      rect.bl = new Vec2(endCoord.x - MINIMUM_GAP, endCoord.y + MINIMUM_GAP);
    }
  } else if (edge.dirVec.y > 0) {
    rect.tr = new Vec2(startCoord.x + MINIMUM_GAP, startCoord.y - MINIMUM_GAP);
    rect.bl = new Vec2(endCoord.x - MINIMUM_GAP, endCoord.y + MINIMUM_GAP);
  } else {
    rect.tr = new Vec2(endCoord.x + MINIMUM_GAP, endCoord.y - MINIMUM_GAP);
    rect.bl = new Vec2(startCoord.x - MINIMUM_GAP, startCoord.y + MINIMUM_GAP);
  }

  return rect;
};

/**
 * Finds the opposing edge of the shape from the same skeleton segment.
 */
const findParallelEdge = (
  shape: DrawingShape,
  edge: DrawingEdge
): DrawingEdge | undefined => {
  const skStartIndex = shape.cornerMap[edge.startCornerId].skIndex;
  const skEndIndex = shape.cornerMap[edge.endCornerId].skIndex;

  return shape.drawingEdges.find(
    (testEdge) =>
      testEdge !== edge &&
      shape.cornerMap[testEdge.startCornerId].skIndex === skEndIndex &&
      shape.cornerMap[testEdge.endCornerId].skIndex === skStartIndex
  );
};

// ============================================================================
// ARROW CONSTRAINT HELPERS
// ============================================================================

/**
 * Clears any previously calculated constraints from all arrows.
 */
const clearArrowConstraints = (shape: DrawingShape): void => {
  shape.arrowsInfo?.forEach((arrow) => {
    arrow.maximumLength = undefined;
    arrow.minimumLength = undefined;
  });
};

/**
 * Attempts to build constraint context for an arrow.
 * Returns undefined if any required data is missing.
 */
const tryBuildArrowContext = (
  curArrow: ArrowInfo,
  shape: DrawingShape
): ArrowConstraintContext | undefined => {
  if (!curArrow.drawingEdgeId) {
    return undefined;
  }

  const curEdge = findDrawingEdgeById(curArrow.drawingEdgeId, shape);

  if (!curEdge || !curArrow.size) {
    return undefined;
  }

  const parallelEdge = findParallelEdge(shape, curEdge);

  if (!parallelEdge) {
    return undefined;
  }

  const parallelArrow = findArrowByEdgeId(parallelEdge.id, shape);

  if (!parallelArrow || !parallelArrow.size) {
    return undefined;
  }

  return {
    curArrow,
    curEdge,
    parallelEdge,
    parallelArrow,
    skIndex: getSkeletonIndex(shape, curEdge),
    shape,
  };
};

/**
 * Determines if minimum gap should be enforced based on corner angles.
 */
const shouldEnforceMinimumGap = (context: ArrowConstraintContext): boolean => {
  const { skIndex, shape } = context;

  if (!isInteriorSkeletonEdge(skIndex, shape.skeleton.length)) {
    return false;
  }

  const precedingSide = subtractVectors(
    shape.skeleton[skIndex].pos,
    shape.skeleton[skIndex - 1].pos
  );
  const succeedingSide = subtractVectors(
    shape.skeleton[skIndex + 1].pos,
    shape.skeleton[skIndex + 2].pos
  );

  return (
    angleBetween(precedingSide, succeedingSide) < MINIMUM_ANGLE_TO_ENFORCE_GAP
  );
};

/**
 * Applies minimum length constraints to arrows.
 */
const applyMinimumLengthConstraints = (
  context: ArrowConstraintContext,
  enforceMinGap: boolean
): void => {
  const { curArrow, parallelArrow, parallelEdge, shape } = context;

  const sizeDifference = roundToNearest(
    (curArrow.size as number) - (parallelArrow.size as number),
    ARROW_LENGTH_GRANULARITY_IN_INCHES
  );

  if (enforceMinGap) {
    curArrow.minimumLength = sizeDifference + MINIMUM_GAP;
    parallelArrow.minimumLength =
      MINIMUM_GAP - adjustEdgeForDiagonals(shape, parallelEdge);
  } else {
    curArrow.minimumLength = sizeDifference + EDGE_LENGTH_INPUT_LIMIT.MIN;
  }
};

/**
 * Processes constraints for a single arrow.
 */
const processArrowConstraint = (
  curArrow: ArrowInfo,
  shape: DrawingShape
): void => {
  const context = tryBuildArrowContext(curArrow, shape);

  if (!context) {
    return;
  }

  const enforceMinGap = shouldEnforceMinimumGap(context);

  applyMinimumLengthConstraints(context, enforceMinGap);
  context.curArrow.maximumLength = calculateEdgeMaximums(
    shape,
    context.curEdge
  );
};

// ============================================================================
// EDGE MAXIMUM HELPERS
// ============================================================================

/**
 * Builds context for edge maximum calculation.
 * Returns undefined if shape is invalid or edge is not at skeleton endpoint.
 */
const tryBuildEdgeMaxContext = (
  shape: DrawingShape,
  edge: DrawingEdge
): EdgeMaxContext | undefined => {
  if (!shape?.drawingEdges) {
    return undefined;
  }

  const edgeSkIndex = getSkeletonIndex(shape, edge);

  // Edge must be at beginning or end of skeleton
  if (edgeSkIndex !== 0 && edgeSkIndex !== shape.skeleton.length - 2) {
    return undefined;
  }

  return {
    shape,
    edge,
    edgeSkIndex,
    isBeginningOfSkeleton: edgeSkIndex === 0,
    startCoord: shape.cornerMap[edge.startCornerId].coords,
    endCoord: shape.cornerMap[edge.endCornerId].coords,
    isHorizontalEdge: Math.abs(edge.dirVec.x) > Math.abs(edge.dirVec.y),
  };
};

/**
 * Creates direction extractor function based on edge orientation.
 */
const createDirectionExtractor = (
  isHorizontalEdge: boolean
): DirectionExtractor =>
  isHorizontalEdge
    ? (point: Vec2) => roundToArrowLengthGranularity(point.x)
    : (point: Vec2) => roundToArrowLengthGranularity(point.y);

/**
 * Checks if test edge is within 3 skeleton lengths (too close).
 */
const isTestEdgeTooClose = (
  testEdge: DrawingEdge,
  edgeSkIndex: number,
  shape: DrawingShape
): boolean => Math.abs(getSkeletonIndex(shape, testEdge) - edgeSkIndex) < 3;

/**
 * Determines if skeleton is moving towards the test edge.
 */
const isMovingTowardsTestEdge = (
  context: EdgeMaxContext,
  testEdge: DrawingEdge,
  dirXy: DirectionExtractor
): boolean => {
  const { shape, isBeginningOfSkeleton } = context;
  const testEdgeCoord = dirXy(shape.cornerMap[testEdge.startCornerId].coords);

  if (isBeginningOfSkeleton) {
    const skeletonDir =
      dirXy(shape.skeleton[1].pos) - dirXy(shape.skeleton[0].pos);
    const edgeToTestDir = dirXy(shape.skeleton[0].pos) - testEdgeCoord;

    return isSameSign(skeletonDir, edgeToTestDir);
  }

  const lastIdx = shape.skeleton.length - 1;
  const skeletonDir =
    dirXy(shape.skeleton[lastIdx - 1].pos) - dirXy(shape.skeleton[lastIdx].pos);
  const edgeToTestDir = dirXy(shape.skeleton[lastIdx].pos) - testEdgeCoord;

  return isSameSign(skeletonDir, edgeToTestDir);
};

/**
 * Checks if edge can collide with keep-out zone (axis overlap).
 */
const canCollideWithKeepOutZone = (
  context: EdgeMaxContext,
  keepOutRect: KeepOutRectangle
): boolean => {
  const { startCoord, isHorizontalEdge } = context;

  if (isHorizontalEdge) {
    return startCoord.y >= keepOutRect.tr.y && startCoord.y <= keepOutRect.bl.y;
  }

  return startCoord.x >= keepOutRect.bl.x && startCoord.x <= keepOutRect.tr.x;
};

/**
 * Calculates minimum clearance distance from edge to keep-out zone.
 */
const calculateDistanceToKeepOut = (
  context: EdgeMaxContext,
  keepOutRect: KeepOutRectangle,
  dirXy: DirectionExtractor
): number => {
  const { startCoord, endCoord } = context;

  const distFromStart = Math.min(
    Math.abs(dirXy(startCoord) - dirXy(keepOutRect.tr)),
    Math.abs(dirXy(startCoord) - dirXy(keepOutRect.bl))
  );

  const distFromEnd = Math.min(
    Math.abs(dirXy(endCoord) - dirXy(keepOutRect.tr)),
    Math.abs(dirXy(endCoord) - dirXy(keepOutRect.bl))
  );

  return Math.max(distFromStart, distFromEnd);
};

/**
 * Updates clearance to minimum of current and new distance.
 * Preserves falsy 0 behavior: if distance is 0, returns unchanged clearance.
 */
const updateClearance = (
  currentClearance: number | undefined,
  distance: number
): number | undefined => {
  if (!distance) {
    return currentClearance;
  }

  if (currentClearance === undefined) {
    return distance;
  }

  return Math.min(currentClearance, distance);
};

/**
 * Processes a single test edge for clearance calculation.
 */
const processTestEdgeForClearance = (
  context: EdgeMaxContext,
  testEdge: DrawingEdge,
  currentClearance: number | undefined
): number | undefined => {
  const { shape, edgeSkIndex, isHorizontalEdge } = context;

  if (isTestEdgeTooClose(testEdge, edgeSkIndex, shape)) {
    return currentClearance;
  }

  const keepOutRect = buildKeepOutRectangle(testEdge, shape);
  const dirXy = createDirectionExtractor(isHorizontalEdge);

  if (!isMovingTowardsTestEdge(context, testEdge, dirXy)) {
    return currentClearance;
  }

  if (!canCollideWithKeepOutZone(context, keepOutRect)) {
    return currentClearance;
  }

  const distance = calculateDistanceToKeepOut(context, keepOutRect, dirXy);

  return updateClearance(currentClearance, distance);
};

// ============================================================================
// MAIN EXPORTED FUNCTIONS
// ============================================================================

/**
 * Calculates the maximum length an edge can extend without collision.
 * Only applies to edges at skeleton endpoints.
 *
 * @param shape - The drawing shape containing the edge
 * @param edge - The edge to calculate maximum length for
 * @returns Maximum extension distance, or undefined if no constraint
 */
const calculateEdgeMaximums = (
  shape: DrawingShape,
  edge: DrawingEdge
): number | undefined => {
  const context = tryBuildEdgeMaxContext(shape, edge);

  if (!context) {
    return undefined;
  }

  return shape.drawingEdges.reduce<number | undefined>(
    (clearance, testEdge) =>
      processTestEdgeForClearance(context, testEdge, clearance),
    undefined
  );
};

/**
 * Calculates and assigns minimum and maximum length constraints for all edges.
 * Mutates the shape's arrowsInfo by setting minimumLength and maximumLength.
 *
 * @param shape - The drawing shape to calculate constraints for (mutated)
 */
export const calculateEdgeConstraints = (shape: DrawingShape): void => {
  if (!shape.arrowsInfo) {
    return;
  }

  clearArrowConstraints(shape);
  shape.arrowsInfo.forEach((arrow) => processArrowConstraint(arrow, shape));
};

interface enforceEdgeConstraintsParams {
  arrow: ArrowInfo;
  value: number;
  edgeType?: SideItemOption;
  shape: DrawingShape;
}

/**
 * Returns the sum of the adjustments to the maximum length for the given edge due to adjacent side widths
 * @param shape the shape we're dealing with (to get the cornerMap)
 * @param edge the edge in question
 */
export const getAdjacentSideWidths = (
  shape: DrawingShape,
  edge?: DrawingEdge
): number => {
  if (!edge) {
    return 0;
  }

  return Object.values(shape.cornerMap)
    .filter((corner) =>
      [edge.startCornerId, edge.endCornerId].includes(corner.id)
    )
    .sort((a, b) => a.skIndex - b.skIndex)
    .reduce((acc, corner, index) => {
      if (!corner.angle || corner.angle >= 0) {
        return acc;
      }

      // adjust for the lower index bone
      const adjacentSkIndex = corner.skIndex - (index === 0 ? 1 : 0);

      return acc + shape.skeleton[adjacentSkIndex].depth * 2;
    }, 0);
};

/**
 * Returns the sum of the adjustments to the maximum length for the given edge due to diagonal corners
 * @param shape the shape we're dealing with (to get the cornerMap)
 * @param edge the edge in question
 */
export const getDiagonalAdjustmentsForEdge = (
  shape: DrawingShape,
  edge?: DrawingEdge
): number => {
  if (!edge) {
    return 0;
  }

  const corners = Object.values(shape.cornerMap);

  return [edge.startCornerId, edge.endCornerId].reduce((acc, id) => {
    const corner = corners.find((c) => c.id === id);
    const cornerAdjust = corner?.diagonalLength
      ? corner.diagonalLength / Math.sqrt(2)
      : 0;

    return acc + cornerAdjust;
  }, 0);
};

const getAdjustedEdgeValueByEdgeType = ({
  arrow,
  value = 0,
  edgeType,
  shape,
}: enforceEdgeConstraintsParams): number => {
  if (edgeType && !arrow.label) {
    const limitBy: Record<SideItemOption, typeof BACKSPLASH_INPUT_LIMIT> = {
      [SideItemOption.BACKSPLASH]: BACKSPLASH_INPUT_LIMIT,
      [SideItemOption.WATERFALL]: WATERFALL_INPUT_LIMIT,
    };

    return clampWithLimit(value, limitBy[edgeType]);
  }

  const edge = shape.drawingEdges.find(
    (edge) => edge.id === arrow.drawingEdgeId
  );
  const adjacentSideWidths = getAdjacentSideWidths(shape, edge);
  const diagonalLength = getDiagonalAdjustmentsForEdge(shape, edge);
  const isRadiusOrBumpOrDiagonal =
    arrow.isRadiusOut || arrow.isBumpOut || arrow.isDiagonalCorner;

  return clampWithLimit(value, {
    MIN: isRadiusOrBumpOrDiagonal ? 0 : EDGE_LENGTH_INPUT_LIMIT.MIN,
    MAX: EDGE_LENGTH_INPUT_LIMIT.MAX - (adjacentSideWidths + diagonalLength),
  });
};

const applyArrowMinMaxConstraints = ({
  arrow,
  value,
}: enforceEdgeConstraintsParams): number =>
  clampWithLimit(value, {
    MIN: arrow.minimumLength ?? -Infinity,
    MAX: arrow.maximumLength ?? Infinity,
  });

export const enforceEdgeConstraints = ({
  arrow,
  value = 0,
  edgeType,
  shape,
}: enforceEdgeConstraintsParams): number =>
  roundToArrowLengthGranularity(
    applyArrowMinMaxConstraints({
      arrow,
      value: getAdjustedEdgeValueByEdgeType({ arrow, value, edgeType, shape }),
      shape,
    })
  );

export const enforceRadiusConstraints = (
  cornerInfo: CornerInfo,
  value = 0
): number => {
  if (value < DEFAULT_CORNER_RADIUS) {
    return DEFAULT_CORNER_RADIUS;
  }

  const maxAllowedRadius = cornerInfo.maxAllowedRadius ?? 0;

  if (!maxAllowedRadius) {
    console.error(
      'Tried to enforceRadiusConstraints without a maxAllowedRadius',
      cornerInfo
    );
  }

  const maxRadius = Math.min(value, maxAllowedRadius);

  return maxRadius < DEFAULT_CORNER_RADIUS
    ? DEFAULT_CORNER_RADIUS
    : roundToCornerRadiusGranularity(maxRadius); //round to the nearest quarter inch
};

// populates max allowed radius for a corner
export const calcMaxAllowedRadius = (
  shape: DrawingShape,
  corner: CornerInfo
): void => {
  // this needs lots of work for radius out and bump outs
  const incidentEdges = shape.drawingEdges.filter(
    (edge) => edge.startCornerId === corner.id || edge.endCornerId === corner.id
  );
  let maxAllowedRadius = Infinity;

  incidentEdges.forEach((edge) => {
    const oppositeCornerId =
      edge.startCornerId === corner.id ? edge.endCornerId : edge.startCornerId;
    const oppositeCorner = shape.cornerMap[oppositeCornerId];
    let radiusSize = 0;

    if (corner.size && (isBumpOut(edge) || isRadiusOut(edge))) {
      radiusSize = corner.size;
    } else {
      const limit = oppositeCorner.size
        ? maxAvailableForCornerOptions(oppositeCorner)
        : DEFAULT_CORNER_RADIUS;

      radiusSize = edge.dirVec.length() - limit;
    }

    maxAllowedRadius = Math.min(maxAllowedRadius, radiusSize);
  });

  corner.maxAllowedRadius = maxAllowedRadius;
};

/**
 * find the maximum available size of either a radius or diagonal corner
 * @param corner
 * @param radius
 */
export const maxAvailableForCornerOptions = (
  corner: CornerInfo,
  radius = 0
): number => {
  if (!corner.angle) {
    return 0;
  }

  const tanRadians = Math.tan(convertToRadians(corner.angle / 2));

  if (radius !== 0) {
    // the radius argument is used when validating a corner can take a particular radius value
    return Math.abs(radius / tanRadians);
  }

  if (corner.diagonalLength) {
    return calculateIsoscelesTriangleSide(corner.angle, corner.diagonalLength);
  }

  // if no radius argument is passed, the intent is in calculating preset constriants on radii
  if (corner.size) {
    return roundToCornerRadiusGranularity(Math.abs(corner.size / tanRadians));
  }

  return 0;
};
